import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
    Bell,
    CheckCheck,
    FileText,
    Megaphone,
    MessageCircle,
    MessageSquareReply,
    PackagePlus,
    Pencil,
    ShoppingCart,
    UserCheck,
    UserPlus,
    X,
    XCircle,
} from 'lucide-react';

import {
    Avatar,
    AvatarFallback,
} from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import type { ApiNotification } from '@/lib/notifications-api';
import {
    fetchNotifications,
    fetchUnreadCount,
    getNotificationOrderUuid,
    markAllNotificationsRead,
    markNotificationRead,
} from '@/lib/notifications-api';
import { extractArray } from '@/lib/shared-utils';
import { cn } from '@/lib/utils';

interface BadgeStyle {
    bg: string;
    Icon: LucideIcon;
}

function formatTimeAgo(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
}

function getOrderAction(n: ApiNotification): string {
    const p = n.payload as Record<string, unknown> | null;
    if (p?.action) return p.action as string;
    if (n.kind.startsWith('order_')) return n.kind.replace('order_', '');
    return '';
}

function getOrderBadge(action: string): BadgeStyle {
    if (action === 'created') return { bg: 'bg-success', Icon: PackagePlus };
    if ([
        'supplier_accepted', 'customer_accepted',
        'accepted', 'confirmed', 'completed',
    ].includes(action)) {
        return { bg: 'bg-success', Icon: CheckCheck };
    }
    if ([
        'supplier_declined', 'customer_rejected',
        'declined', 'cancelled',
    ].includes(action)) {
        return { bg: 'bg-destructive', Icon: XCircle };
    }
    if (['supplier_modified', 'modified', 'modified_by_supplier'].includes(action)) {
        return { bg: 'bg-warning', Icon: Pencil };
    }
    return { bg: 'bg-primary', Icon: ShoppingCart };
}

function getBadge(n: ApiNotification): BadgeStyle {
    switch (n.kind) {
        case 'connection_request':
            return { bg: 'bg-primary', Icon: UserPlus };
        case 'connection_accepted':
            return { bg: 'bg-success', Icon: UserCheck };
        case 'announcement_created':
            return { bg: 'bg-warning', Icon: Megaphone };
        case 'quotation_requested':
            return { bg: 'bg-primary', Icon: FileText };
        case 'quotation_responded':
            return { bg: 'bg-success', Icon: MessageSquareReply };
        case 'whatsapp_message_received':
            return { bg: 'bg-success', Icon: MessageCircle };
        case 'whatsapp_message_failed':
        case 'whatsapp_disconnected':
            return { bg: 'bg-destructive', Icon: MessageCircle };
        default:
            if (n.kind.startsWith('order_')) return getOrderBadge(getOrderAction(n));
            return { bg: 'bg-destructive', Icon: X };
    }
}

// Notification bodies come from the server referencing the actor's
// username. Swap in the actor's business name when one is set, across
// every notification kind.
function getNotificationBody(n: ApiNotification): string {
    const { username, business_name } = n.actor ?? {};
    if (business_name && username && n.body.includes(username)) {
        return n.body.replaceAll(username, business_name);
    }
    return n.body;
}

function getInitial(n: ApiNotification): string {
    const label = n.actor?.business_name || n.actor?.username || '?';
    return label.charAt(0).toUpperCase();
}

function getNotificationRoute(n: ApiNotification): string {
    const payload = n.payload as Record<string, unknown> | null | undefined;
    switch (n.kind) {
        case 'connection_request':
        case 'connection_rejected':
            return '/related-parties/requests';
        case 'connection_accepted':
            return `/related-parties/party/${n.actor.id}`;
        case 'announcement_created':
            return '/announcements';
        case 'quotation_requested':
            return '/quotations/respond';
        case 'quotation_responded':
            return '/quotations/request';
        case 'whatsapp_message_received':
        case 'whatsapp_message_failed':
        case 'whatsapp_disconnected':
            return '/chat';
        default:
            break;
    }
    if (n.kind.startsWith('order_')) {
        const uuid = getNotificationOrderUuid(n);
        if (!uuid) return '/orders';
        const action = (payload?.action as string) ?? n.kind.replace('order_', '');
        const supplierActions = ['created', 'customer_accepted', 'customer_rejected', 'pending'];
        return supplierActions.includes(action)
            ? `/respond-orders/${uuid}`
            : `/orders/${uuid}`;
    }
    return '/dashboard';
}

export function NotificationsMenu() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [tab, setTab] = useState<'all' | 'unread'>('all');

    const { data: notifRes } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => fetchNotifications({ page_size: 20 }),
        refetchInterval: 30000,
    });
    const { data: countRes } = useQuery({
        queryKey: ['notifications-unread-count'],
        queryFn: fetchUnreadCount,
        refetchInterval: 15000,
    });

    const allNotifications: ApiNotification[] = extractArray(notifRes?.data);
    const unreadCount = countRes?.ok ? (countRes.data?.unread_count ?? 0) : 0;
    const notifications = tab === 'unread'
        ? allNotifications.filter((n) => !n.is_read)
        : allNotifications;

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    };

    const markReadMutation = useMutation({
        mutationFn: (id: number) => markNotificationRead(id),
        onSuccess: invalidate,
    });
    const markAllMutation = useMutation({
        mutationFn: markAllNotificationsRead,
        onSuccess: invalidate,
    });

    const openNotification = (n: ApiNotification) => {
        if (!n.is_read) markReadMutation.mutate(n.id);
        navigate(getNotificationRoute(n));
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-9 w-9 rounded-full"
                >
                    <Bell className="h-5 w-5 text-muted-foreground" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-card">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-[360px] p-0 bg-card border-border/60 shadow-xl rounded-2xl overflow-hidden"
                align="end"
            >
                <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
                    <p className="text-lg font-bold tracking-tight">Notifications</p>
                    {unreadCount > 0 && (
                        <button
                            type="button"
                            onClick={() => markAllMutation.mutate()}
                            className="flex items-center gap-1 text-xs text-primary hover:underline"
                            disabled={markAllMutation.isPending}
                        >
                            {markAllMutation.isPending
                                ? <Spinner className="h-3 w-3" />
                                : <CheckCheck className="h-3.5 w-3.5" />}
                            Mark all read
                        </button>
                    )}
                </div>

                <div className="flex items-center gap-2 px-3 pb-2">
                    <button
                        type="button"
                        onClick={() => setTab('all')}
                        className={cn(
                            'rounded-full px-3 py-1 text-sm font-semibold transition-colors',
                            tab === 'all'
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-accent',
                        )}
                    >
                        All
                    </button>
                    <button
                        type="button"
                        onClick={() => setTab('unread')}
                        className={cn(
                            'rounded-full px-3 py-1 text-sm font-semibold transition-colors',
                            tab === 'unread'
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:bg-accent',
                        )}
                    >
                        Unread
                    </button>
                </div>

                {notifications.length === 0 ? (
                    <div className="py-10 px-3 text-sm text-muted-foreground text-center">
                        {tab === 'unread' ? "You're all caught up" : 'No notifications yet'}
                    </div>
                ) : (
                    <div className="max-h-[26rem] overflow-y-auto pb-1">
                        {notifications.map((n) => {
                            const badge = getBadge(n);
                            const BadgeIcon = badge.Icon;
                            return (
                                <DropdownMenuItem
                                    key={n.id}
                                    className={cn(
                                        'flex items-start gap-3 px-4 py-2.5 cursor-pointer rounded-none focus:bg-accent',
                                        !n.is_read && 'bg-primary/5',
                                    )}
                                    onClick={() => openNotification(n)}
                                >
                                    <div className="relative shrink-0">
                                        <Avatar className="h-11 w-11">
                                            <AvatarFallback className="bg-muted text-foreground text-sm font-semibold">
                                                {getInitial(n)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className={cn(
                                            'absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-card',
                                            badge.bg,
                                        )}
                                        >
                                            <BadgeIcon className="h-3 w-3 text-primary-foreground" />
                                        </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className={cn(
                                            'text-sm leading-snug line-clamp-2 whitespace-normal',
                                            !n.is_read
                                                ? 'font-medium text-foreground'
                                                : 'text-foreground/90',
                                        )}
                                        >
                                            {getNotificationBody(n)}
                                        </p>
                                        <p className={cn(
                                            'text-xs mt-0.5',
                                            !n.is_read
                                                ? 'text-primary font-medium'
                                                : 'text-muted-foreground',
                                        )}
                                        >
                                            {formatTimeAgo(n.created_at)}
                                        </p>
                                    </div>
                                    {!n.is_read && (
                                        <span className="mt-2 h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
                                    )}
                                </DropdownMenuItem>
                            );
                        })}
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
